import type { IconName } from '@/components/Icon'

/**
 * The element types, as stored in the legend.
 */
export type QuestionType =
  | 'Bool'
  | 'Select'
  | 'Numeric'
  | 'Text'
  | 'DateTime'
  | 'Breaker'
  | 'Documents'
  | 'Path'
  | 'Variable'
  | 'Effect'
  | 'InventoryItem'
  | 'PersonalOrganizerPrefill'
  | 'PersonalOrganizerCollectedValue'

/**
 * How an element type shows in the builder.
 */
export interface QuestionTypeInfo {
  type: QuestionType
  friendlyType: string
  viewOrder: number
  /**
   * A square in `iconColor`.
   */
  iconName: IconName
  iconColor: string
  /**
   * Prefix for generated IDs of new elements of the type.
   */
  idPrefix: string
  /**
   * Meta elements shape the legend without asking the client anything.
   */
  isMetaElement?: boolean
  /**
   * Deprecated types can't be added, and their elements load and save as they
   * are, without editing.
   */
  deprecated?: boolean
  collapsible?: boolean
  /**
   * Nothing references these, so they have no ID.
   */
  optionalId?: boolean
}

/**
 * Each element type's info.
 */
export const questionTypeInfo: Record<QuestionType, QuestionTypeInfo> = {
  Bool: {
    type: 'Bool',
    friendlyType: 'Yes/No',
    viewOrder: 1,
    iconColor: '#0967B9',
    iconName: 'legend-builder-yes-no',
    idPrefix: 'QUESTION_',
  },
  Select: {
    type: 'Select',
    friendlyType: 'Select',
    viewOrder: 2,
    iconColor: '#007BE7',
    iconName: 'legend-builder-choice',
    idPrefix: 'QUESTION_',
  },
  Numeric: {
    type: 'Numeric',
    friendlyType: 'Numeric',
    viewOrder: 3,
    iconColor: '#5AB3FF',
    iconName: 'legend-builder-numeric',
    idPrefix: 'QUESTION_',
  },
  Text: {
    type: 'Text',
    friendlyType: 'Text',
    viewOrder: 3,
    iconColor: '#40A0F4',
    iconName: 'legend-builder-text',
    idPrefix: 'QUESTION_',
  },
  DateTime: {
    type: 'DateTime',
    friendlyType: 'Date & Time',
    viewOrder: 4,
    iconColor: '#4EC4FF',
    iconName: 'legend-builder-datetime',
    idPrefix: 'QUESTION_',
  },
  Breaker: {
    type: 'Breaker',
    friendlyType: 'Breaker',
    viewOrder: 5,
    iconColor: '#645EFE',
    iconName: 'legend-builder-breaker',
    idPrefix: 'Breaker_',
  },
  Documents: {
    type: 'Documents',
    friendlyType: 'Documents',
    viewOrder: 6,
    iconColor: '#0967B9',
    iconName: 'legend-builder-document-upload',
    idPrefix: 'QUESTION_',
    deprecated: true,
  },
  Path: {
    type: 'Path',
    friendlyType: 'Path',
    viewOrder: 1,
    iconColor: '#4C00DC',
    iconName: 'legend-builder-path',
    idPrefix: 'PATH_',
    isMetaElement: true,
    collapsible: true,
  },
  Variable: {
    type: 'Variable',
    friendlyType: 'Variable',
    viewOrder: 2,
    iconColor: '#763EF4',
    iconName: 'legend-builder-variable',
    idPrefix: 'VARIABLE_',
    isMetaElement: true,
  },
  Effect: {
    type: 'Effect',
    friendlyType: 'Effect',
    viewOrder: 3,
    iconColor: '#763EF4',
    iconName: 'legend-builder-effect',
    idPrefix: 'QUESTION_',
    isMetaElement: true,
    optionalId: true,
  },
  InventoryItem: {
    type: 'InventoryItem',
    friendlyType: 'Service Item',
    viewOrder: 4,
    iconColor: '#763EF4',
    iconName: 'legend-builder-inventory-item',
    idPrefix: 'INVENTORY_ITEM_',
    isMetaElement: true,
  },
  PersonalOrganizerPrefill: {
    type: 'PersonalOrganizerPrefill',
    friendlyType: 'Organizer Prefill',
    viewOrder: 5,
    iconColor: '#0891B2',
    iconName: 'legend-builder-personal-organizer-prefill',
    idPrefix: 'QUESTION_',
    isMetaElement: true,
    optionalId: true,
  },
  PersonalOrganizerCollectedValue: {
    type: 'PersonalOrganizerCollectedValue',
    friendlyType: 'Organizer Collected Value',
    viewOrder: 6,
    iconColor: '#0891B2',
    iconName: 'legend-builder-personal-organizer-collected-value',
    idPrefix: 'COLLECTED_VALUE_',
    isMetaElement: true,
  },
}

const sortedTypes = Object.values(questionTypeInfo)
  .filter((t) => !t.deprecated)
  .sort((a, b) => a.viewOrder - b.viewOrder)

/**
 * The toolbox's two sections, in display order.
 */
export const toolboxTypes = {
  elements: sortedTypes.filter((t) => !t.isMetaElement),
  meta: sortedTypes.filter((t) => t.isMetaElement),
}
